import { wait } from '@/common/utils/wait';

export interface RateLimiter {
  /** Serialises tasks; guarantees >= minIntervalMs between two task STARTS. */
  schedule<T>(task: () => Promise<T>, signal?: AbortSignal): Promise<T>;
  readonly pending: number;
}

/**
 * Error thrown for a queued task whose signal fired before it reached the front
 * of the queue. Such a task is dropped, never sent.
 */
class AbortedBeforeStartError extends Error {
  readonly name = 'AbortError';

  constructor() {
    super('Task was aborted before it started');
  }
}

/**
 * Serialises tasks through a single promise tail and spaces their STARTS by at
 * least `minIntervalMs`. Start-to-start spacing means a slow task adds no extra
 * gap for the task behind it.
 *
 * Used to honour hard third-party rate limits (Nominatim allows max 1 req/s).
 * Create one instance per host at module scope.
 *
 * `minIntervalMs` may be a getter so a module-scope limiter can still follow a
 * configuration value that is overridden after import (Storybook, tests).
 */
export function createRateLimiter(minIntervalMs: number | (() => number)): RateLimiter {
  const getMinInterval = () =>
    typeof minIntervalMs === 'function' ? minIntervalMs() : minIntervalMs;

  let tail: Promise<unknown> = Promise.resolve();
  let lastStartedAt = Number.NEGATIVE_INFINITY;
  let pending = 0;

  return {
    get pending() {
      return pending;
    },

    schedule<T>(task: () => Promise<T>, signal?: AbortSignal): Promise<T> {
      pending += 1;

      const run = async (): Promise<T> => {
        try {
          // A superseded task must never reach the network.
          if (signal?.aborted) throw new AbortedBeforeStartError();

          const elapsed = Date.now() - lastStartedAt;
          const delay = getMinInterval() - elapsed;
          if (delay > 0) await wait(delay);

          if (signal?.aborted) throw new AbortedBeforeStartError();

          lastStartedAt = Date.now();
          return await task();
        } finally {
          pending -= 1;
        }
      };

      const result = tail.then(run, run);
      // Chain on both outcomes so a rejected task never stalls the queue, while
      // the promise handed back to the caller still surfaces the rejection.
      tail = result.then(
        () => undefined,
        () => undefined
      );

      return result;
    },
  };
}
